import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { AlertCircle, ChevronDown, ChevronUp, ClipboardCheck, CreditCard, HelpCircle, Link2, Plus } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { BenefitRow, CardArt, Eyebrow, QuarterRail } from '@/components/perks/PerkVisuals'
import { useBenefitActions } from '@/components/perks/useBenefitActions'
import { usePerkDialogs } from '@/components/perks/usePerkDialogs'
import { errorMessage, perksApi, usePerks, usePerksCatalog, usePerksMutation } from '@/lib/perksApi'
import {
  allItems,
  capturedThisCardYear,
  daysBetween,
  enrollmentNeeded,
  linkSuggestions,
  money,
  nextAnniversary,
  openItems,
  parseDay,
  plural,
  quarterName,
  quarterOf,
  shortDay,
  unusedThisQuarter,
  type PerkCard,
} from '@/lib/perks'

const VISIBLE_OPEN = 8

export function PerksPage() {
  const navigate = useNavigate()
  const { data: state, isLoading, isError } = usePerks()
  const { data: catalog } = usePerksCatalog()
  const perks = usePerkDialogs(state, (id) => navigate(`/perks/cards/${id}`))
  const { markUsed } = useBenefitActions()
  const mutate = usePerksMutation()
  const [showAll, setShowAll] = useState(false)

  const header = (
    <div className="flex items-center justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Card Perks</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">Use every statement credit before it resets.</p>
      </div>
      <Button onClick={perks.addCard}>
        <Plus className="mr-2 h-4 w-4" />
        Add card
      </Button>
    </div>
  )

  if (isLoading) {
    return (
      <div className="space-y-6">
        {header}
        <div className="grid gap-4 md:grid-cols-3">
          <Skeleton className="h-48 md:col-span-2" />
          <Skeleton className="h-48" />
        </div>
        <Skeleton className="h-80" />
      </div>
    )
  }

  if (isError || !state) {
    return (
      <div className="space-y-6">
        {header}
        <Card>
          <CardContent className="py-12 text-center text-sm text-gray-500">Couldn’t load your card perks. Try again in a moment.</CardContent>
        </Card>
      </div>
    )
  }

  if (state.cards.length === 0) {
    const unlinked = state.credit_accounts.filter((a) => a.card_id === null)
    return (
      <div className="space-y-6">
        {header}
        <Card>
          <CardContent className="grid gap-8 p-8 md:grid-cols-[1fr_320px]">
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Stop leaving credits on the table</h2>
              <p className="max-w-prose text-gray-600 dark:text-gray-400">
                Track Resy, lululemon, hotel and travel credits on every card. See what’s left this quarter, what each card has
                paid back against its fee, and let Chequey spot statement credits on your linked cards.
              </p>
              {unlinked.length > 0 && (
                <div className="space-y-1">
                  <Eyebrow>Your linked cards</Eyebrow>
                  {unlinked.slice(0, 4).map((a) => {
                    const template = catalog?.templates.find((t) => t.id === a.suggested_template_id) ?? null
                    return (
                      <button
                        key={a.account_id}
                        type="button"
                        onClick={() => (template ? perks.addCardFrom({ template, account: a }) : perks.addCard())}
                        className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800/60"
                      >
                        <CreditCard className="h-4 w-4 text-gray-400" />
                        <span className="font-medium text-gray-900 dark:text-gray-100">{a.name}</span>
                        <span className="text-sm text-gray-500">{template ? `looks like ${template.issuer} ${template.name}` : a.mask && `•• ${a.mask}`}</span>
                      </button>
                    )
                  })}
                </div>
              )}
              <Button onClick={perks.addCard}>
                <Plus className="mr-2 h-4 w-4" />
                Add a card
              </Button>
            </div>
            <div className="hidden space-y-[-3.5rem] md:block" aria-hidden>
              <CardArt art="platinum" issuer="American Express" name="Platinum Card" className="rotate-[-4deg]" />
              <CardArt art="sapphire" issuer="Chase" name="Sapphire Reserve" className="relative translate-x-6 rotate-[3deg]" />
            </div>
          </CardContent>
        </Card>
        {perks.dialogs}
      </div>
    )
  }

  const today = new Date()
  const quarter = quarterOf(today)
  const unused = unusedThisQuarter(state, today)
  const open = openItems(state, today)
  const inQuarter = open.filter((i) => i.benefit.kind === 'credit' && i.status.period.end <= quarter.end)
  const quarterDaysLeft = daysBetween(today, quarter.end)
  const missing = allItems(state, today).filter((i) => i.creditLooksMissing)
  const enroll = enrollmentNeeded(state)
  const links = linkSuggestions(state)
  const attention = state.unmatched_credits.length + missing.length + (enroll.length > 0 ? 1 : 0) + links.length
  const visible = showAll ? open : open.slice(0, VISIBLE_OPEN)
  const capturedTotal = state.cards.reduce((sum, c) => sum + capturedThisCardYear(c, today), 0)
  const feeTotal = state.cards.reduce((sum, c) => sum + c.annual_fee_cents, 0)

  return (
    <div className="space-y-6">
      {header}

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="md:col-span-2">
          <CardContent className="space-y-5 p-6">
            <div>
              <Eyebrow>Unused this quarter</Eyebrow>
              <div className="mt-1 text-4xl font-semibold tabular-nums text-gray-900 dark:text-gray-100">{money(unused.cents)}</div>
              <div className="mt-1 text-sm text-gray-500">
                {unused.count === 0
                  ? `Everything due this quarter is used. ${quarterName(quarter)} ends in ${quarterDaysLeft} days.`
                  : `${plural(unused.count, 'credit')} · ${quarterName(quarter)} ends in ${quarterDaysLeft} days`}
              </div>
            </div>
            <QuarterRail quarter={quarter} today={today} items={inQuarter} />
            <div className="grid grid-cols-3 gap-4 border-t border-gray-100 pt-4 dark:border-gray-800">
              <HeroStat label="Back this card year" value={money(capturedTotal)} tone={capturedTotal > 0 ? 'good' : undefined} />
              <HeroStat label="Annual fees" value={money(feeTotal)} />
              <HeroStat
                label={capturedTotal >= feeTotal ? 'Ahead of fees' : 'To cover fees'}
                value={money(Math.abs(feeTotal - capturedTotal))}
                tone={capturedTotal >= feeTotal ? 'good' : undefined}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Needs a look</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 pt-0">
            {attention === 0 && <p className="py-4 text-sm text-gray-500">All clear. Credits on your linked cards are matched and every benefit is enrolled.</p>}
            {links.map(({ card, account }) => (
              <AttentionRow
                key={`link-${card.id}`}
                icon={<Link2 className="h-4 w-4 text-primary-500" />}
                title={`Link ${card.name}${account.mask ? ` to •• ${account.mask}` : ''}`}
                subtitle="Picks up its statement credits automatically"
                onClick={async () => {
                  try {
                    await mutate(() => perksApi.updateCard(card.id, { account_id: account.account_id }))
                    toast.success(`${card.name} linked`)
                  } catch (e) {
                    toast.error(errorMessage(e))
                  }
                }}
              />
            ))}
            {state.unmatched_credits.slice(0, 4).map((c) => (
              <AttentionRow
                key={c.transaction_id}
                icon={<HelpCircle className="h-4 w-4 text-primary-500" />}
                title={c.name}
                subtitle={`+${money(c.amount_cents)} · ${state.cards.find((k) => k.id === c.card_id)?.name ?? 'Card'} · ${shortDay(parseDay(c.date))}`}
                onClick={() => perks.assignCredit(c)}
              />
            ))}
            {missing.slice(0, 3).map((i) => (
              <AttentionRow
                key={i.benefit.id}
                icon={<AlertCircle className="h-4 w-4 text-danger" />}
                title={`${i.benefit.name} credit hasn’t posted`}
                subtitle={`Bought ${i.waitingOn ? shortDay(parseDay(i.waitingOn.date)) : ''} · check enrollment`}
                onClick={() => perks.openBenefit(i.benefit.id)}
              />
            ))}
            {enroll.length > 0 && (
              <AttentionRow
                icon={<ClipboardCheck className="h-4 w-4 text-gray-600 dark:text-gray-300" />}
                title={`Enroll ${plural(enroll.length, 'benefit')}`}
                subtitle={enroll.slice(0, 3).map((e) => e.benefit.name).join(', ') + (enroll.length > 3 ? ` +${enroll.length - 3}` : '')}
                onClick={perks.openEnrollment}
              />
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle>Ending soonest</CardTitle>
            <span className="text-sm tabular-nums text-gray-500">{open.length}</span>
          </CardHeader>
          <CardContent className="pt-0">
            {open.length === 0 && <p className="py-6 text-sm text-gray-500">Every credit is used for now.</p>}
            <div className="-mx-3">
              {visible.map((item) => (
                <BenefitRow key={item.benefit.id} item={item} onOpen={() => perks.openBenefit(item.benefit.id)} onQuickUse={() => markUsed(item)} />
              ))}
            </div>
            {open.length > VISIBLE_OPEN && (
              <Button variant="ghost" size="sm" className="mt-2 text-primary-600" onClick={() => setShowAll(!showAll)}>
                {showAll ? <ChevronUp className="mr-1 h-4 w-4" /> : <ChevronDown className="mr-1 h-4 w-4" />}
                {showAll ? 'Show less' : `${open.length - VISIBLE_OPEN} more`}
              </Button>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle>Your cards</CardTitle>
            <span className="text-sm text-gray-500">Back vs. fee</span>
          </CardHeader>
          <CardContent className="space-y-1 pt-0">
            {state.cards.map((card) => (
              <CardSummaryRow key={card.id} card={card} today={today} onClick={() => navigate(`/perks/cards/${card.id}`)} />
            ))}
            <Button variant="ghost" size="sm" className="mt-1 text-primary-600" onClick={perks.addCard}>
              <Plus className="mr-1 h-4 w-4" /> Add a card
            </Button>
          </CardContent>
        </Card>
      </div>

      <p className="text-xs text-gray-400">
        Statement credits on linked cards are picked up when your accounts sync. In-app credits like Uber Cash you mark yourself.
        Reminders come from the Chequey iOS app.
        {catalog?.reviewed && ` Card terms reviewed ${parseDay(catalog.reviewed).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}.`}
      </p>

      {perks.dialogs}
    </div>
  )
}

function HeroStat({ label, value, tone }: { label: string; value: string; tone?: 'good' }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className={tone === 'good' ? 'mt-0.5 text-lg font-semibold tabular-nums text-success' : 'mt-0.5 text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-100'}>{value}</div>
    </div>
  )
}

function AttentionRow({ icon, title, subtitle, onClick }: { icon: React.ReactNode; title: string; subtitle: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="-mx-2 flex w-[calc(100%+1rem)] items-start gap-3 rounded-lg px-2 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800/60">
      <span className="mt-0.5">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">{title}</span>
        <span className="block truncate text-xs text-gray-500">{subtitle}</span>
      </span>
    </button>
  )
}

export function CardSummaryRow({ card, today, onClick }: { card: PerkCard; today: Date; onClick: () => void }) {
  const captured = capturedThisCardYear(card, today)
  const fraction = card.annual_fee_cents > 0 ? captured / card.annual_fee_cents : 0
  return (
    <button type="button" onClick={onClick} className="-mx-2 w-[calc(100%+1rem)] rounded-lg px-2 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/60">
      <div className="flex items-center gap-3">
        <CardArt art={card.art} mini />
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-gray-900 dark:text-gray-100">{card.name}</div>
          <div className="truncate text-xs text-gray-500">
            {card.annual_fee_cents > 0 ? `${money(card.annual_fee_cents)} fee · renews ${shortDay(nextAnniversary(parseDay(card.opened_on), today))}` : 'No annual fee'}
          </div>
        </div>
        <div className="text-right">
          <div className={captured > 0 ? 'font-medium tabular-nums text-success' : 'tabular-nums text-gray-400'}>{money(captured)}</div>
          <div className="text-xs text-gray-400">back</div>
        </div>
      </div>
      {card.annual_fee_cents > 0 && (
        <div className="mt-2 h-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
          <div className={fraction >= 1 ? 'h-full bg-success' : 'h-full bg-gray-500'} style={{ width: `${Math.max(Math.min(fraction, 1) * 100, 0.5)}%` }} />
        </div>
      )}
    </button>
  )
}
